import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { ITaxComplianceValues } from 'features/Partners/types'
import { GTPartnerTaxInformationPersonTypeInput } from 'gql/types.generated'

const nameMessages = defineMessages({
  required: {
    id: 'partners.new.name.required',
    defaultMessage: 'The name is required'
  },
  max: {
    id: 'partners.new.name.max',
    defaultMessage: 'The name must be at most {max} characters'
  }
})

const taxPersonTypeMessages = defineMessages({
  required: {
    id: 'partners.new.taxPersonType.required',
    defaultMessage: 'The tax person type is required'
  }
})

export const taxIdMessages = defineMessages({
  required: {
    id: 'partners.new.taxId.required',
    defaultMessage: 'The RFC is required'
  },
  max: {
    id: 'partners.new.taxId.max',
    defaultMessage: 'The RFC must be at most {max} characters'
  },
  documentRequired: {
    id: 'partners.new.taxIdDocument.required',
    defaultMessage: 'The RFC document is required'
  }
})

const curpMessages = defineMessages({
  max: {
    id: 'partners.new.curp.max',
    defaultMessage: 'The CURP must be at most {max} characters'
  }
})

const taxRegimeMessages = defineMessages({
  max: {
    id: 'partners.new.taxRegime.max',
    defaultMessage: 'The tax regime must be at most {max} characters'
  }
})

const taxSituationLinkMessages = defineMessages({
  url: {
    id: 'partners.new.taxSituationLink.url',
    defaultMessage: 'The tax situation link must be a valid URL'
  },
  max: {
    id: 'partners.new.taxSituationLink.max',
    defaultMessage: 'The tax situation link must be at most {max} characters'
  }
})

const repseIdMessages = defineMessages({
  max: {
    id: 'partners.new.repseId.max',
    defaultMessage: 'The REPSE ID must be at most {max} characters'
  },
  required: {
    id: 'partners.new.repseId.required',
    defaultMessage: 'The REPSE ID is required'
  }
})

const repseLinkMessages = defineMessages({
  url: {
    id: 'partners.new.repseLink.url',
    defaultMessage: 'The REPSE link must be a valid URL'
  },
  max: {
    id: 'partners.new.repseLink.max',
    defaultMessage: 'The REPSE link must be at most {max} characters'
  },
  required: {
    id: 'partners.new.repseLink.required',
    defaultMessage: 'The REPSE link is required'
  }
})

export const schema = (intl: IntlShape): yup.SchemaOf<ITaxComplianceValues> => {
  return yup.object().shape({
    taxPersonType: yup.mixed().required(intl.formatMessage(taxPersonTypeMessages.required)),
    companyName: yup.string().when('taxPersonType', {
      is: GTPartnerTaxInformationPersonTypeInput.LegalEntity,
      then: yup
        .string()
        .max(128, ({ max }) => intl.formatMessage(nameMessages.max, { max }))
        .required(intl.formatMessage(nameMessages.required))
    }),
    businessName: yup.string().when('taxPersonType', {
      is: GTPartnerTaxInformationPersonTypeInput.LegalEntity,
      then: yup.string().max(128, ({ max }) => intl.formatMessage(nameMessages.max, { max }))
    }),
    taxId: yup
      .string()
      .max(13, ({ max }) => intl.formatMessage(taxIdMessages.max, { max }))
      .required(intl.formatMessage(taxIdMessages.required)),
    curp: yup.string().max(18, ({ max }) => intl.formatMessage(curpMessages.max, { max })),
    usePartnerName: yup.boolean().required(),
    taxIdDocument: yup.object().shape({
      id: yup.string(),
      name: yup.string(),
      path: yup.string(),
      file: yup.mixed()
    }),
    curpDocument: yup.object().shape({
      file: yup.mixed().nullable(),
      id: yup.string(),
      name: yup.string(),
      path: yup.string()
    }),
    taxRegime: yup
      .string()
      .max(3, ({ max }) => intl.formatMessage(taxRegimeMessages.max, { max }))
      .optional(),
    proofOfTaxSituation: yup
      .string()
      .url(intl.formatMessage(taxSituationLinkMessages.url))
      .max(256, ({ max }) => intl.formatMessage(taxSituationLinkMessages.max, { max }))
      .optional(),
    repseId: yup
      .string()
      .when('taxPersonType', {
        is: GTPartnerTaxInformationPersonTypeInput.LegalEntity,
        then: yup.string().required(intl.formatMessage(repseIdMessages.required)),
        otherwise: yup.string().optional()
      })
      .max(16, ({ max }) => intl.formatMessage(repseIdMessages.max, { max })),
    repseLink: yup
      .string()
      .when('taxPersonType', {
        is: GTPartnerTaxInformationPersonTypeInput.LegalEntity,
        then: yup.string().required(intl.formatMessage(repseLinkMessages.required)),
        otherwise: yup.string().optional()
      })
      .url(intl.formatMessage(repseLinkMessages.url))
      .max(256, ({ max }) => intl.formatMessage(repseLinkMessages.max, { max }))
  })
}
